import { PortInStep } from "../portInStep";
import { useWatch } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import type { FieldError, FieldName, FieldValues, UseFormMethods } from "react-hook-form";
import { ValidationCodeView } from "./ValidationCodeView";
import { useValidationCode } from "./useValidationCode";
import { getObjectByPath } from "@utils/getObjectByPath";

interface IParams<TFieldValues extends FieldValues = FieldValues> {
    phoneNumberSelected: string;
    setIsFinished(newValue: boolean): void;
    updatePortInStep(nextStep: PortInStep): void;
    setHasTooManyAttempt(hasTooManyAttempt: boolean): void;
    formMethods: UseFormMethods<TFieldValues>;
    namePrefix?: string;
    clearCardError?(): void;
}

export const ValidationCode = <TFieldValues extends FieldValues = FieldValues>(props: IParams<TFieldValues>) => {
    const {
        formMethods,
        phoneNumberSelected,
        setIsFinished,
        updatePortInStep,
        setHasTooManyAttempt,
        namePrefix,
        clearCardError,
    } = props;
    const validationCodeFieldName = namePrefix ? `${namePrefix}validationCode` : "validationCode";
    const fieldError = getObjectByPath<FieldError>(formMethods.errors, validationCodeFieldName);

    const validationCodeValue = useWatch<string>({
        name: validationCodeFieldName,
        control: formMethods.control,
        defaultValue: "",
    });

    const { validateCode, validateCodeState } = useValidationCode({
        validationCodeValue,
        phoneNumberSelected,
        formMethods,
        namePrefix,
        updatePortInStep,
        setIsFinished,
        setHasTooManyAttempt,
    });

    const [apiErrorMessage, setApiErrorMessage] = useState("");

    useEffect(() => {
        setApiErrorMessage(validateCodeState.error?.errorMessage ?? "");
    }, [validateCodeState.error]);

    const skipClearServerErrorOnMount = useRef(true);

    useEffect(() => {
        if (skipClearServerErrorOnMount.current) {
            skipClearServerErrorOnMount.current = false;

            return;
        }

        setApiErrorMessage("");
    }, [validationCodeValue]);

    const retryCode = () => {
        updatePortInStep(PortInStep.getCode);
    };

    return (
        <ValidationCodeView
            error={fieldError}
            errorMessage={apiErrorMessage}
            isLoading={validateCodeState.isLoading}
            namePrefix={namePrefix}
            register={formMethods.register}
            retryCode={retryCode}
            validateCode={validateCode}
            onClearError={() => {
                formMethods.clearErrors(validationCodeFieldName as FieldName<TFieldValues>);
                clearCardError?.();
            }}
        />
    );
};
